<template>
    <Head title="Create PM Work Order" />

    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="mb-6">
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Create PM Work Order</h1>
                    <p class="mt-1 text-sm text-gray-600">Generate a preventive maintenance work order from PM schedule</p>
                </div>
                <Link
                    :href="route('settings.maintenance.assets.show', asset.id)"
                    class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-50"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Asset
                </Link>
            </div>
        </div>

        <!-- PM Plan Info Card -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3 flex-1">
                    <h3 class="text-sm font-medium text-blue-800">PM Plan: {{ pmPlan.name }}</h3>
                    <div class="mt-2 text-sm text-blue-700">
                        <p class="mb-1"><strong>Asset:</strong> {{ asset.name }} ({{ asset.asset_tag }})</p>
                        <p><strong>Description:</strong> {{ pmPlan.description || 'No description' }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="submit" class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div class="p-6 space-y-6">
                <!-- Title -->
                <div>
                    <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Title <span class="text-red-500">*</span>
                    </label>
                    <input
                        id="title"
                        v-model="form.title"
                        type="text"
                        required
                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500': form.errors.title }"
                    />
                    <p v-if="form.errors.title" class="mt-1 text-sm text-red-600">{{ form.errors.title }}</p>
                </div>

                <!-- Description -->
                <div>
                    <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Description <span class="text-red-500">*</span>
                    </label>
                    <textarea
                        id="description"
                        v-model="form.description"
                        rows="4"
                        required
                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500': form.errors.description }"
                    ></textarea>
                    <p v-if="form.errors.description" class="mt-1 text-sm text-red-600">{{ form.errors.description }}</p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Type (Locked) -->
                    <div>
                        <label for="type" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Type
                        </label>
                        <input
                            id="type"
                            type="text"
                            value="Preventive Maintenance"
                            disabled
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 cursor-not-allowed"
                        />
                        <p class="mt-1 text-xs text-gray-500">Type is automatically set to Preventive Maintenance for PM work orders</p>
                    </div>

                    <!-- Priority -->
                    <div>
                        <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Priority <span class="text-red-500">*</span>
                        </label>
                        <select
                            id="priority"
                            v-model="form.priority"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.priority }"
                        >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>
                        <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600">{{ form.errors.priority }}</p>
                    </div>
                </div>

                <!-- Service Category & Service (Optional) -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Service Category -->
                    <div>
                        <label for="service_category_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Service Category (Optional)
                        </label>
                        <select
                            id="service_category_id"
                            v-model="form.service_category_id"
                            @change="onCategoryChange"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.service_category_id }"
                        >
                            <option value="">Select Category (Optional)</option>
                            <option
                                v-for="category in serviceCategories"
                                :key="category.id"
                                :value="category.id"
                            >
                                {{ category.name }}
                            </option>
                        </select>
                        <p v-if="form.errors.service_category_id" class="mt-1 text-sm text-red-600">{{ form.errors.service_category_id }}</p>
                    </div>

                    <!-- Service (Cascading based on category) -->
                    <div>
                        <label for="service_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Service (Optional)
                        </label>
                        <select
                            id="service_id"
                            v-model="form.service_id"
                            :disabled="!form.service_category_id || availableServices.length === 0"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            :class="{ 'border-red-500': form.errors.service_id }"
                        >
                            <option value="">Select Service (Optional)</option>
                            <option
                                v-for="service in availableServices"
                                :key="service.id"
                                :value="service.id"
                            >
                                {{ service.name }}
                            </option>
                        </select>
                        <p v-if="form.errors.service_id" class="mt-1 text-sm text-red-600">{{ form.errors.service_id }}</p>
                        <p v-if="form.service_category_id && availableServices.length === 0" class="mt-1 text-sm text-gray-500">
                            No services available for this category
                        </p>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Asset (Locked) -->
                    <div>
                        <label for="asset" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Asset
                        </label>
                        <input
                            id="asset"
                            type="text"
                            :value="`${asset.name} (${asset.asset_tag})`"
                            disabled
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 cursor-not-allowed"
                        />
                        <p class="mt-1 text-xs text-gray-500">Asset cannot be changed for PM work orders</p>
                    </div>

                    <!-- Location (Locked) -->
                    <div>
                        <label for="location" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Location
                        </label>
                        <input
                            id="location"
                            type="text"
                            :value="prefilledData.location_name || 'No location'"
                            disabled
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 cursor-not-allowed"
                        />
                        <p class="mt-1 text-xs text-gray-500">Location cannot be changed for PM work orders</p>
                    </div>
                </div>

                <!-- Scheduled Dates -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Start Date -->
                    <div>
                        <label for="scheduled_start" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Scheduled Start Date <span class="text-red-500">*</span>
                        </label>
                        <input
                            id="scheduled_start"
                            v-model="form.scheduled_start"
                            type="date"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.scheduled_start }"
                        />
                        <p v-if="form.errors.scheduled_start" class="mt-1 text-sm text-red-600">{{ form.errors.scheduled_start }}</p>
                    </div>

                    <!-- End Date -->
                    <div>
                        <label for="scheduled_end" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Scheduled End Date <span class="text-red-500">*</span>
                        </label>
                        <input
                            id="scheduled_end"
                            v-model="form.scheduled_end"
                            type="date"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.scheduled_end }"
                        />
                        <p v-if="form.errors.scheduled_end" class="mt-1 text-sm text-red-600">{{ form.errors.scheduled_end }}</p>
                    </div>
                </div>

                <!-- Estimates -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Estimated Hours -->
                    <div>
                        <label for="estimated_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Estimated Hours
                        </label>
                        <input
                            id="estimated_hours"
                            v-model="form.estimated_hours"
                            type="number"
                            step="0.25"
                            min="0"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.estimated_hours }"
                        />
                        <p v-if="form.errors.estimated_hours" class="mt-1 text-sm text-red-600">{{ form.errors.estimated_hours }}</p>
                    </div>

                    <!-- Estimated Cost -->
                    <div>
                        <label for="estimated_cost" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Estimated Cost
                        </label>
                        <input
                            id="estimated_cost"
                            v-model="form.estimated_cost"
                            type="number"
                            step="0.01"
                            min="0"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'border-red-500': form.errors.estimated_cost }"
                        />
                        <p v-if="form.errors.estimated_cost" class="mt-1 text-sm text-red-600">{{ form.errors.estimated_cost }}</p>
                    </div>
                </div>

                <!-- Assignment -->
                <div>
                    <label for="assigned_to" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Assign To (Optional)
                    </label>
                    <select
                        id="assigned_to"
                        v-model="form.assigned_to"
                        class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500': form.errors.assigned_to }"
                    >
                        <option value="">Not Assigned</option>
                        <option
                            v-for="technician in technicians"
                            :key="technician.id"
                            :value="technician.id"
                        >
                            {{ technician.name }}
                        </option>
                    </select>
                    <p v-if="form.errors.assigned_to" class="mt-1 text-sm text-red-600">{{ form.errors.assigned_to }}</p>
                    <p class="mt-1 text-xs text-gray-500">Assigned technician will receive notification via email and in-app</p>
                </div>

                <!-- Tasks Checklist -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Task Checklist
                    </label>
                    <div class="space-y-2">
                        <div
                            v-for="(task, index) in form.tasks"
                            :key="index"
                            class="flex items-center space-x-2"
                        >
                            <input
                                v-model="form.tasks[index]"
                                type="text"
                                class="flex-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="Enter task description"
                            />
                            <button
                                v-if="form.tasks.length > 1"
                                type="button"
                                @click="removeTask(index)"
                                class="text-red-600 hover:text-red-800"
                            >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <button
                        type="button"
                        @click="addTask"
                        class="mt-2 inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-700"
                    >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Task
                    </button>
                    <p v-if="form.errors.tasks" class="mt-1 text-sm text-red-600">{{ form.errors.tasks }}</p>
                </div>
            </div>

            <!-- Actions -->
            <div class="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
                <Link
                    :href="route('settings.maintenance.assets.show', asset.id)"
                    class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md font-semibold text-xs text-gray-700 uppercase tracking-widest hover:bg-gray-50"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 disabled:opacity-50"
                >
                    <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Create PM Work Order
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

const props = defineProps({
    schedule: Object,
    pmPlan: Object,
    asset: Object,
    technicians: Array,
    serviceCategories: Array,
    prefilledData: Object,
});

const toast = useToast();

const form = useForm({
    pm_schedule_id: props.prefilledData.pm_schedule_id,
    title: props.prefilledData.title || '',
    description: props.prefilledData.description || '',
    priority: props.prefilledData.priority || 'normal',
    asset_id: props.prefilledData.asset_id,
    location_id: props.prefilledData.location_id,
    service_category_id: '',
    service_id: '',
    assigned_to: '',
    scheduled_start: props.prefilledData.scheduled_start || '',
    scheduled_end: props.prefilledData.scheduled_end || '',
    estimated_hours: props.prefilledData.estimated_hours || '',
    estimated_cost: props.prefilledData.estimated_cost || '',
    tasks: props.prefilledData.tasks && props.prefilledData.tasks.length > 0 ? props.prefilledData.tasks : [''],
});

// Get available services based on selected category
const availableServices = computed(() => {
    if (!form.service_category_id) return [];
    const category = props.serviceCategories.find(c => c.id === form.service_category_id);
    return category ? category.services : [];
});

// When category changes, reset service selection
const onCategoryChange = () => {
    form.service_id = '';
};

const addTask = () => {
    form.tasks.push('');
};

const removeTask = (index) => {
    form.tasks.splice(index, 1);
};

const submit = () => {
    // Filter out empty tasks
    form.tasks = form.tasks.filter(task => task.trim() !== '');

    form.post(route('work-orders.storeFromPmSchedule'), {
        onSuccess: () => {
            toast.success('PM work order created successfully');
        },
        onError: () => {
            toast.error('Failed to create PM work order. Please check the form.');
        },
    });
};
</script>
